import { useMemo } from 'react';
import { ScrollView, Text, View, useWindowDimensions } from 'react-native';
import {
  AlertTriangle,
  BarChart3,
  CalendarClock,
  CreditCard,
  PieChart as PieIcon,
  ShoppingBag,
  Stethoscope,
  TrendingDown,
  Truck,
  Wallet,
} from 'lucide-react-native';

import { Card, EmptyState, SkeletonRows } from '@/components/ui';
import { Pressable } from 'react-native';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import { todosMovimientosQuery } from '@/services/firebase/contabilidad';
import { productosVentaQuery, insumosInternosQuery } from '@/services/firebase/inventario';
import { ventasPetshop } from '@/services/firebase/petshops';
import { colors, radius, typography } from '@/theme/tokens';
import { formatBs } from '@/utils/format';

const NOMBRES_MES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

export function EstadisticasScreen() {
  const { width } = useWindowDimensions();
  const wide = width >= 920;

  const movimientos = useFirestoreCollection(todosMovimientosQuery);
  const productos = useFirestoreCollection(productosVentaQuery);
  const insumos = useFirestoreCollection(insumosInternosQuery);
  const ventasDanna = useFirestoreCollection(ventasPetshop('danna'));
  const ventasBruno = useFirestoreCollection(ventasPetshop('bruno'));

  // 1. Gastos por mes (últimos 6 meses)
  const gastosPorMes = useMemo(() => {
    const ahora = new Date();
    const buckets: { label: string; key: string; total: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(ahora.getFullYear(), ahora.getMonth() - i, 1);
      buckets.push({
        label: `${NOMBRES_MES[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`,
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        total: 0,
      });
    }
    movimientos.data.forEach((m) => {
      if (m.tipo !== 'egreso') return;
      if ((m.estadoPago ?? 'pagado') === 'pendiente') return;
      const f = m.fecha.toDate();
      const k = `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}`;
      const b = buckets.find((x) => x.key === k);
      if (b) b.total += m.monto;
    });
    return buckets;
  }, [movimientos.data]);

  // 2. Top categorías de gasto (mes actual)
  const topCategorias = useMemo(() => {
    const ahora = new Date();
    const map = new Map<string, number>();
    movimientos.data.forEach((m) => {
      if (m.tipo !== 'egreso') return;
      if ((m.estadoPago ?? 'pagado') === 'pendiente') return;
      const f = m.fecha.toDate();
      if (f.getFullYear() !== ahora.getFullYear() || f.getMonth() !== ahora.getMonth()) return;
      map.set(m.categoria, (map.get(m.categoria) ?? 0) + m.monto);
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [movimientos.data]);

  // 3. Top proveedores (mes actual)
  const topProveedores = useMemo(() => {
    const ahora = new Date();
    const map = new Map<string, number>();
    movimientos.data.forEach((m) => {
      if (m.tipo !== 'egreso' || !m.proveedorNombre) return;
      if ((m.estadoPago ?? 'pagado') === 'pendiente') return;
      const f = m.fecha.toDate();
      if (f.getFullYear() !== ahora.getFullYear() || f.getMonth() !== ahora.getMonth()) return;
      map.set(m.proveedorNombre, (map.get(m.proveedorNombre) ?? 0) + m.monto);
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [movimientos.data]);

  // 4. Desglose por método de pago (mes actual)
  const porMetodoPago = useMemo(() => {
    const ahora = new Date();
    const map: Record<string, number> = {};
    movimientos.data.forEach((m) => {
      if (m.tipo !== 'egreso') return;
      if ((m.estadoPago ?? 'pagado') === 'pendiente') return;
      const f = m.fecha.toDate();
      if (f.getFullYear() !== ahora.getFullYear() || f.getMonth() !== ahora.getMonth()) return;
      map[m.metodoPago] = (map[m.metodoPago] ?? 0) + m.monto;
    });
    return map;
  }, [movimientos.data]);

  const gastoEsteMes = gastosPorMes[gastosPorMes.length - 1]?.total ?? 0;
  const gastoMesAnterior = gastosPorMes[gastosPorMes.length - 2]?.total ?? 0;
  const variacion = gastoMesAnterior > 0 ? ((gastoEsteMes - gastoMesAnterior) / gastoMesAnterior) * 100 : 0;

  // Pendientes de pago
  const pendientesPago = useMemo(() => {
    const pendientes = movimientos.data.filter((m) => m.estadoPago === 'pendiente');
    return {
      cantidad: pendientes.length,
      total: pendientes.reduce((sum, m) => sum + m.monto, 0),
    };
  }, [movimientos.data]);

  // Panorama inventario
  const inventario = useMemo(() => {
    const todos = [...productos.data, ...insumos.data];
    const bajos = todos.filter((p) => p.stockBajo && p.stock > 0).length;
    const agotados = todos.filter((p) => p.stock === 0).length;
    const venceran = todos.filter((p) => {
      const v = p.vencimiento?.toDate?.();
      if (!v) return false;
      const dias = Math.floor((v.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      return dias >= 0 && dias <= 60;
    }).length;
    return { total: todos.length, bajos, agotados, venceran };
  }, [productos.data, insumos.data]);

  // Petshops ingreso de este mes (acumulado)
  const petshopsMes = useMemo(() => {
    const ahora = new Date();
    const enMes = [...ventasDanna.data, ...ventasBruno.data].filter((v) => {
      const f = v.fecha.toDate();
      return f.getFullYear() === ahora.getFullYear() && f.getMonth() === ahora.getMonth();
    });
    return {
      cantidad: enMes.length,
      total: enMes.reduce((sum, v) => sum + v.monto, 0),
    };
  }, [ventasDanna.data, ventasBruno.data]);

  // Petshops por mes — últimos 6 (Danna vs Bruno)
  const petshopsPorMes = useMemo(() => {
    const ahora = new Date();
    const buckets: { label: string; key: string; danna: number; bruno: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(ahora.getFullYear(), ahora.getMonth() - i, 1);
      buckets.push({
        label: `${NOMBRES_MES[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`,
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        danna: 0,
        bruno: 0,
      });
    }
    ventasDanna.data.forEach((v) => {
      const f = v.fecha.toDate();
      const k = `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}`;
      const b = buckets.find((x) => x.key === k);
      if (b) b.danna += v.monto;
    });
    ventasBruno.data.forEach((v) => {
      const f = v.fecha.toDate();
      const k = `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}`;
      const b = buckets.find((x) => x.key === k);
      if (b) b.bruno += v.monto;
    });
    return buckets;
  }, [ventasDanna.data, ventasBruno.data]);

  // Ticket promedio por petshop (mes actual)
  const ticketPromedio = useMemo(() => {
    const ahora = new Date();
    const dannaEnMes = ventasDanna.data.filter((v) => {
      const f = v.fecha.toDate();
      return f.getFullYear() === ahora.getFullYear() && f.getMonth() === ahora.getMonth();
    });
    const brunoEnMes = ventasBruno.data.filter((v) => {
      const f = v.fecha.toDate();
      return f.getFullYear() === ahora.getFullYear() && f.getMonth() === ahora.getMonth();
    });
    const totalDanna = dannaEnMes.reduce((s, v) => s + v.monto, 0);
    const totalBruno = brunoEnMes.reduce((s, v) => s + v.monto, 0);
    return {
      danna: dannaEnMes.length > 0 ? totalDanna / dannaEnMes.length : 0,
      bruno: brunoEnMes.length > 0 ? totalBruno / brunoEnMes.length : 0,
      dannaCantidad: dannaEnMes.length,
      brunoCantidad: brunoEnMes.length,
      dannaTotal: totalDanna,
      brunoTotal: totalBruno,
    };
  }, [ventasDanna.data, ventasBruno.data]);

  const cargando = movimientos.loading || productos.loading || insumos.loading;

  return (
    <ScrollView contentContainerStyle={{ padding: wide ? 28 : 16, gap: 18, paddingBottom: 80 }}>
      {/* Hero */}
      <View
        style={{
          padding: wide ? 24 : 18,
          borderRadius: radius['3xl'],
          backgroundColor: colors.magenta[700],
          // @ts-ignore web only
          backgroundImage: `linear-gradient(135deg, ${colors.magenta[800]} 0%, ${colors.magenta[600]} 100%)`,
          overflow: 'hidden',
        }}
      >
        <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11, fontWeight: typography.weights.bold, letterSpacing: 1.4, textTransform: 'uppercase' }}>
          Estadísticas · clínica
        </Text>
        <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <Text style={{ color: colors.white, fontFamily: typography.display.fontFamily, fontSize: wide ? 36 : 28, fontWeight: typography.weights.bold }}>
            {formatBs(gastoEsteMes)}
          </Text>
          {gastoMesAnterior > 0 ? (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 4,
                paddingHorizontal: 10,
                paddingVertical: 4,
                borderRadius: 999,
                backgroundColor: variacion < 0 ? 'rgba(52,211,153,0.25)' : 'rgba(248,113,113,0.25)',
              }}
            >
              <TrendingDown
                size={12}
                color={variacion < 0 ? '#34D399' : '#F87171'}
                style={{ transform: [{ rotate: variacion < 0 ? '0deg' : '180deg' }] }}
              />
              <Text style={{ fontSize: 11, fontWeight: typography.weights.bold, color: variacion < 0 ? '#34D399' : '#F87171' }}>
                {variacion > 0 ? '+' : ''}{variacion.toFixed(1)}% vs mes anterior
              </Text>
            </View>
          ) : null}
        </View>
        <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, marginTop: 4 }}>
          Gasto acumulado del mes en curso
        </Text>
      </View>

      {/* Stats compactos */}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
        <CompactStat
          label="Pendiente de pago"
          value={String(pendientesPago.cantidad)}
          color={colors.warning[600]}
          icon={<Wallet size={16} color={colors.warning[600]} />}
          hint={pendientesPago.cantidad > 0 ? formatBs(pendientesPago.total) : 'Sin pendientes'}
        />
        <CompactStat
          label="Petshops del mes"
          value={String(petshopsMes.cantidad)}
          color={colors.magenta[700]}
          icon={<ShoppingBag size={16} color={colors.magenta[700]} />}
          hint={formatBs(petshopsMes.total)}
        />
        <CompactStat
          label="Stock crítico"
          value={String(inventario.bajos + inventario.agotados)}
          color={colors.danger[600]}
          icon={<AlertTriangle size={16} color={colors.danger[600]} />}
          hint={`${inventario.agotados} ag. · ${inventario.bajos} bajos`}
        />
        <CompactStat
          label="Vencen ≤60d"
          value={String(inventario.venceran)}
          color={colors.warning[600]}
          icon={<CalendarClock size={16} color={colors.warning[600]} />}
        />
      </View>

      {cargando ? <SkeletonRows rows={3} /> : null}

      {/* Gráfica: gastos por mes */}
      <Card>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <BarChart3 size={20} color={colors.magenta[700]} />
          <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 18, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
            Gastos por mes · últimos 6
          </Text>
        </View>
        <VerticalBars data={gastosPorMes} />
      </Card>

      {/* Top categorías + top proveedores en fila */}
      <View style={{ flexDirection: wide ? 'row' : 'column', gap: 16 }}>
        <Card style={{ flex: 1 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <PieIcon size={20} color={colors.magenta[700]} />
            <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 18, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
              Top categorías · mes actual
            </Text>
          </View>
          {topCategorias.length === 0 ? (
            <EmptyState illustration="note" title="Sin gastos este mes" />
          ) : (
            <HorizontalBars data={topCategorias} color={colors.magenta[700]} />
          )}
        </Card>

        <Card style={{ flex: 1 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <Truck size={20} color={colors.magenta[700]} />
            <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 18, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
              Top proveedores · mes actual
            </Text>
          </View>
          {topProveedores.length === 0 ? (
            <EmptyState illustration="note" title="Sin compras a proveedores este mes" />
          ) : (
            <HorizontalBars data={topProveedores} color={colors.success[600]} />
          )}
        </Card>
      </View>

      {/* Desglose método de pago */}
      <Card>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <CreditCard size={20} color={colors.magenta[700]} />
          <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 18, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
            Método de pago · gastos del mes
          </Text>
        </View>
        <MetodoPagoBar map={porMetodoPago} />
      </Card>

      {/* Gráfica petshops por mes — barras agrupadas Danna vs Bruno */}
      <Card>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <ShoppingBag size={20} color={colors.magenta[700]} />
          <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 18, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
            Ventas petshops · últimos 6 meses
          </Text>
        </View>
        <PetshopGroupedBars data={petshopsPorMes} />
      </Card>

      {/* Ticket promedio petshops */}
      <View style={{ flexDirection: wide ? 'row' : 'column', gap: 16 }}>
        <Card style={{ flex: 1 }}>
          <Text style={{ fontSize: 12, color: colors.ink[500], fontWeight: typography.weights.bold, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 8 }}>
            Petshop Danna · mes actual
          </Text>
          <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 8 }}>
            <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 28, fontWeight: typography.weights.bold, color: colors.magenta[700] }}>
              {formatBs(ticketPromedio.dannaTotal)}
            </Text>
            <Text style={{ fontSize: 13, color: colors.ink[500] }}>total</Text>
          </View>
          <Text style={{ fontSize: 12, color: colors.ink[600], marginTop: 4 }}>
            {ticketPromedio.dannaCantidad} ventas · ticket promedio {formatBs(ticketPromedio.danna)}
          </Text>
        </Card>
        <Card style={{ flex: 1 }}>
          <Text style={{ fontSize: 12, color: colors.ink[500], fontWeight: typography.weights.bold, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 8 }}>
            Petshop Bruno · mes actual
          </Text>
          <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 8 }}>
            <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 28, fontWeight: typography.weights.bold, color: colors.success[600] }}>
              {formatBs(ticketPromedio.brunoTotal)}
            </Text>
            <Text style={{ fontSize: 13, color: colors.ink[500] }}>total</Text>
          </View>
          <Text style={{ fontSize: 12, color: colors.ink[600], marginTop: 4 }}>
            {ticketPromedio.brunoCantidad} ventas · ticket promedio {formatBs(ticketPromedio.bruno)}
          </Text>
        </Card>
      </View>

      {/* Panorama inventario */}
      <Card>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <Stethoscope size={20} color={colors.magenta[700]} />
          <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 18, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
            Panorama de inventario
          </Text>
        </View>
        <View style={{ flexDirection: 'row', gap: 10, flexWrap: 'wrap' }}>
          <InventarioTile color={colors.success[600]} label="OK" value={inventario.total - inventario.bajos - inventario.agotados} total={inventario.total} />
          <InventarioTile color={colors.warning[600]} label="Stock bajo" value={inventario.bajos} total={inventario.total} />
          <InventarioTile color={colors.danger[600]} label="Agotados" value={inventario.agotados} total={inventario.total} />
          <InventarioTile color={colors.warning[600]} label="Vencen pronto" value={inventario.venceran} total={inventario.total} />
        </View>
      </Card>
    </ScrollView>
  );
}

// ----------------------------------------------------------------------------
// Charts internos (sin libraries — solo Views con math)
// ----------------------------------------------------------------------------

function CompactStat({
  label,
  value,
  color,
  icon,
  hint,
}: {
  label: string;
  value: string;
  color: string;
  icon: React.ReactNode;
  hint?: string;
}) {
  return (
    <View
      style={{
        flex: 1,
        minWidth: 160,
        padding: 10,
        borderRadius: radius.xl,
        backgroundColor: colors.white,
        borderWidth: 1,
        borderColor: colors.ink[100],
        borderLeftWidth: 3,
        borderLeftColor: color,
        gap: 4,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
        {icon}
        <Text
          style={{
            fontSize: 10,
            color: colors.ink[500],
            fontWeight: typography.weights.bold,
            letterSpacing: 0.8,
            textTransform: 'uppercase',
          }}
          numberOfLines={1}
        >
          {label}
        </Text>
      </View>
      <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 22, fontWeight: typography.weights.bold, color, lineHeight: 24 }}>
        {value}
      </Text>
      {hint ? (
        <Text style={{ fontSize: 10, color: colors.ink[500] }} numberOfLines={1}>
          {hint}
        </Text>
      ) : null}
    </View>
  );
}

function VerticalBars({ data }: { data: { label: string; total: number }[] }) {
  const max = Math.max(...data.map((d) => d.total), 1);
  return (
    <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 12, height: 160 }}>
      {data.map((d, i) => {
        const heightPct = (d.total / max) * 100;
        return (
          <View key={i} style={{ flex: 1, alignItems: 'center', gap: 6 }}>
            <Text style={{ fontSize: 11, color: colors.ink[600], fontWeight: typography.weights.bold }}>
              {d.total > 0 ? formatBs(d.total).replace('Bs ', '') : ''}
            </Text>
            <View
              style={{
                width: '100%',
                height: `${Math.max(heightPct, 2)}%`,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                backgroundColor: colors.magenta[700],
                // @ts-ignore web only
                backgroundImage: `linear-gradient(180deg, ${colors.magenta[600]} 0%, ${colors.magenta[800]} 100%)`,
                minHeight: 4,
              }}
            />
            <Text style={{ fontSize: 11, color: colors.ink[500], textAlign: 'center' }}>{d.label}</Text>
          </View>
        );
      })}
    </View>
  );
}

function PetshopGroupedBars({ data }: { data: { label: string; danna: number; bruno: number }[] }) {
  const max = Math.max(...data.flatMap((d) => [d.danna, d.bruno]), 1);
  return (
    <View style={{ gap: 8 }}>
      <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 10, height: 160 }}>
        {data.map((d, i) => {
          const dannaHeight = (d.danna / max) * 100;
          const brunoHeight = (d.bruno / max) * 100;
          return (
            <View key={i} style={{ flex: 1, alignItems: 'center', gap: 4 }}>
              <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 3, height: '100%', width: '100%', justifyContent: 'center' }}>
                <View
                  style={{
                    flex: 1,
                    height: `${Math.max(dannaHeight, 2)}%`,
                    borderTopLeftRadius: 6,
                    borderTopRightRadius: 6,
                    backgroundColor: colors.magenta[700],
                    minHeight: 4,
                  }}
                />
                <View
                  style={{
                    flex: 1,
                    height: `${Math.max(brunoHeight, 2)}%`,
                    borderTopLeftRadius: 6,
                    borderTopRightRadius: 6,
                    backgroundColor: colors.success[600],
                    minHeight: 4,
                  }}
                />
              </View>
              <Text style={{ fontSize: 11, color: colors.ink[500], textAlign: 'center' }}>{d.label}</Text>
            </View>
          );
        })}
      </View>
      <View style={{ flexDirection: 'row', gap: 14, justifyContent: 'center' }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <View style={{ width: 10, height: 10, borderRadius: 3, backgroundColor: colors.magenta[700] }} />
          <Text style={{ fontSize: 12, color: colors.ink[700] }}>Danna</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <View style={{ width: 10, height: 10, borderRadius: 3, backgroundColor: colors.success[600] }} />
          <Text style={{ fontSize: 12, color: colors.ink[700] }}>Bruno</Text>
        </View>
      </View>
    </View>
  );
}

function HorizontalBars({ data, color }: { data: [string, number][]; color: string }) {
  const max = Math.max(...data.map((d) => d[1]), 1);
  return (
    <View style={{ gap: 10 }}>
      {data.map(([label, value], i) => {
        const pct = (value / max) * 100;
        return (
          <View key={i} style={{ gap: 4 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
              <Text style={{ fontSize: 13, color: colors.ink[800], fontWeight: typography.weights.semibold, flex: 1 }} numberOfLines={1}>
                {label}
              </Text>
              <Text style={{ fontSize: 13, color: colors.ink[800], fontWeight: typography.weights.bold }}>
                {formatBs(value)}
              </Text>
            </View>
            <View style={{ height: 8, borderRadius: 4, backgroundColor: colors.ink[100], overflow: 'hidden' }}>
              <View
                style={{
                  width: `${pct}%`,
                  height: '100%',
                  borderRadius: 4,
                  backgroundColor: color,
                }}
              />
            </View>
          </View>
        );
      })}
    </View>
  );
}

const METODO_COLOR: Record<string, string> = {
  efectivo: colors.success[600],
  qr: colors.magenta[700],
  transferencia: '#3B82F6',
  tarjeta: '#F59E0B',
  otro: colors.ink[400],
};

const METODO_LABEL: Record<string, string> = {
  efectivo: 'Efectivo',
  qr: 'QR',
  transferencia: 'Transferencia',
  tarjeta: 'Tarjeta',
  otro: 'Otro',
};

function MetodoPagoBar({ map }: { map: Record<string, number> }) {
  const total = Object.values(map).reduce((a, b) => a + b, 0);
  if (total === 0) {
    return <EmptyState illustration="note" title="Sin pagos este mes" />;
  }
  const entries = Object.entries(map).sort((a, b) => b[1] - a[1]);
  return (
    <View style={{ gap: 12 }}>
      {/* Stacked bar */}
      <View style={{ flexDirection: 'row', height: 14, borderRadius: 7, overflow: 'hidden' }}>
        {entries.map(([metodo, value]) => (
          <View
            key={metodo}
            style={{
              width: `${(value / total) * 100}%`,
              backgroundColor: METODO_COLOR[metodo] ?? colors.ink[400],
            }}
          />
        ))}
      </View>
      {/* Leyenda */}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
        {entries.map(([metodo, value]) => (
          <View key={metodo} style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <View
              style={{
                width: 10,
                height: 10,
                borderRadius: 3,
                backgroundColor: METODO_COLOR[metodo] ?? colors.ink[400],
              }}
            />
            <Text style={{ fontSize: 12, color: colors.ink[800], fontWeight: typography.weights.semibold }}>
              {METODO_LABEL[metodo] ?? metodo}
            </Text>
            <Text style={{ fontSize: 12, color: colors.ink[600] }}>
              {formatBs(value)} ({((value / total) * 100).toFixed(0)}%)
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
}

function InventarioTile({ label, value, total, color }: { label: string; value: number; total: number; color: string }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <View
      style={{
        flex: 1,
        minWidth: 140,
        padding: 14,
        borderRadius: radius.xl,
        backgroundColor: colors.crema[50],
        borderLeftWidth: 3,
        borderLeftColor: color,
        gap: 4,
      }}
    >
      <Text style={{ fontSize: 11, color: colors.ink[500], fontWeight: typography.weights.bold, letterSpacing: 0.8, textTransform: 'uppercase' }}>
        {label}
      </Text>
      <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 24, fontWeight: typography.weights.bold, color }}>
        {value}
      </Text>
      <Text style={{ fontSize: 11, color: colors.ink[500] }}>
        {pct.toFixed(0)}% del inventario
      </Text>
    </View>
  );
}
